// Brighter Futures Music Festival hub page.
// Not yet linked in the main nav — accessible via /festival for internal review.
// Data-driven artist lineup, sponsor grid, and community partner grid so the
// client can add items in follow-up work without redesigning the page.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Ticket,
  Calendar,
  Clock,
  MapPin,
  ArrowRight,
  Heart,
  Users,
  Star,
  Music,
  Sparkles,
  Instagram,
  ExternalLink,
  Store,
  HandHeart,
  Palette,
  Baby,
  UtensilsCrossed,
  Map,
  ChevronDown,
  ChevronUp,
  Bell,
  Handshake,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import PageShell from '../components/PageShell';
import SEO from '../components/SEO';
import GivebutterModal, { GivebutterScript } from '../components/GivebutterModal';
import { JoyUnderline, JoySticker, JoyDotsField } from '../components/JoyAccents';
import { useI18n } from '../contexts/I18nContext';

const TICKETS_WIDGET = 'gBZ6E6';
const VENDORS_WIDGET = 'prWrye';
const SPONSORSHIP_WIDGET = 'g8M7VR';

// Sections that are hidden until content arrives. Flip to `true` to re-enable.
// Layouts are preserved below the flag — no rebuild needed when logos come in.
const SHOW_SPONSOR_RECOGNITION = false;
const SHOW_COMMUNITY_PARTNERS = false;

// TEMPORARY: Saturday, August 1 programming has been canceled.
// Setting this to `false` restores Saturday everywhere: lineup filter tab, day card,
// artist cards, vendor dates, and copy. All artist bios, photos, and data remain in place.
const SATURDAY_CANCELED = true;

const FESTIVAL_START = '2026-07-30T18:00:00-04:00';
const SAT_START = '2026-08-01T10:00:00-04:00';

// ─── Days ─────────────────────────────────────────────────────────
// Copy is looked up via i18n at render time (keys: festival.day.<key>.*).
const DAYS = [
  { key: 'thu', color: '#FCD968', accent: 'yellow' },
  { key: 'sat', color: '#E5614C', accent: 'coral' },
  { key: 'sun', color: '#5DCAB5', accent: 'teal' },
];

// ─── Artist lineup ────────────────────────────────────────────────
// Names are proper nouns (kept as-is). `tag` and `bio` come from i18n
// via key: festival.artist.<slug>.tag / festival.artist.<slug>.bio.
// Set `hasBio: false` when a bio is pending — the card hides the toggle.
const ARTISTS = [
  // Thursday
  { slug: 'gracemoon', name: 'Grace S. Moon', day: 'thu', photo: '/festival/artists/grace-moon.jpg', hasBio: false },
  // Saturday
  { slug: 'bridgeside', name: 'The Bridgeside Cypher', day: 'sat', photo: '/festival/artists/bridgeside-cypher.jpg' },
  { slug: 'likmeraki', name: 'Lik Meraki', day: 'sat', photo: '/festival/artists/lik-meraki.jpg' },
  { slug: 'chuck', name: 'The Incredible Chuck', day: 'sat', photo: '/festival/artists/incredible-chuck.jpg' },
  { slug: 'dl', name: 'DL', day: 'sat', photo: '/festival/artists/dl.jpg' },
  { slug: 'terry', name: 'Terry Borderline', day: 'sat', photo: '/festival/artists/terry-borderline.jpg' },
  { slug: 'massspiritz', name: 'Mass Spiritz', day: 'sat', photo: '/festival/artists/mass-spiritz.jpg' },
  { slug: 'monkstemple', name: "Monk's Temple Records", day: 'sat', photo: '/festival/artists/monks-temple.jpg' },
  { slug: 'mathematics', name: 'Mathematics', day: 'sat', photo: '/festival/artists/mathematics.jpg' },
  { slug: 'exitfame', name: 'Exit Fame', day: 'sat', photo: '/festival/artists/exit-fame.jpg' },
  { slug: 'termanology', name: 'Termanology', day: 'sat' },
  { slug: 'headliners', name: 'Headliners Entertainment', day: 'sat', photo: '/festival/artists/headliners-entertainment.jpg' },
  // Sunday
  { slug: 'edwin', name: 'Edwin Pabón y Su Orquesta', day: 'sun', featured: true, photo: '/festival/artists/edwin-pabon.jpg' },
  { slug: 'dania', name: 'Dania La Catrachita', day: 'sun', photo: '/festival/artists/dania.jpg' },
  { slug: 'menino', name: 'O Menino do Vale', day: 'sun', photo: '/festival/artists/menino-do-vale.jpg' },
  { slug: 'sondelsol', name: 'Son del Sol', day: 'sun', photo: '/festival/artists/son-del-sol.jpg' },
  { slug: 'efxto', name: 'EFXTO', day: 'sun', photo: '/festival/artists/efxto.jpg' },
  { slug: 'coqui', name: 'Coqui', day: 'sun', photo: '/festival/artists/coqui.jpg', hasBio: false },
  { slug: 'chocolate', name: 'Chocolate', day: 'sun', photo: '/festival/artists/chocolate.jpg', hasBio: false },
];

// ─── Sunday schedule (fully-detailed from client doc) ─────────────
// Copy comes from i18n via key: festival.sched.s<n>.label / .sub / .body
const SUNDAY_SCHEDULE = [
  { slot: 's1', image: '/festival/schedule/salsa.jpg', imageAltKey: 'festival.sched.salsaAlt' },
  { slot: 's2' },
  { slot: 's3' },
  { slot: 's4' },
  { slot: 's5', placeholder: true },
];

// Confirmed vendors — items appear inside the Vendors section as they are confirmed.
// Grid gracefully hides when this array is empty.
// `bio` comes from i18n via key: festival.vendor.<slug>.bio
const CONFIRMED_VENDORS = [
  { slug: 'grace', name: 'Grace Is Sweet LLC', logo: '/festival/vendors/grace-is-sweet.jpg' },
];

// Copy comes from i18n via key: festival.sponsor.l<n>.name / .desc
const SPONSORSHIP_LEVELS = [
  { key: 'l1', emoji: '🌱', amount: '$250', color: 'teal' },
  { key: 'l2', emoji: '🎨', amount: '$500', color: 'coral' },
  { key: 'l3', emoji: '🤝', amount: '$1,000', color: 'yellow' },
  { key: 'l4', emoji: '🎶', amount: '$2,500', color: 'coral' },
  { key: 'l5', emoji: '☀️', amount: '$5,000', color: 'teal', featured: true },
  { key: 'l6', emoji: '💎', amount: '$10,000+', color: 'yellow' },
];

// Copy comes from i18n via key: festival.family.<slot>.title / .desc
const FAMILY_ACTIVITIES = [
  { slot: 'art', icon: Palette },
  { slot: 'kids', icon: Baby },
  { slot: 'food', icon: UtensilsCrossed },
  { slot: 'circles', icon: HandHeart },
  { slot: 'stages', icon: Music },
  { slot: 'partners', icon: Users },
];

// Copy comes from i18n via key: festival.faq.q<n> / .a<n>
const FAQ_ITEMS = [1, 2, 3, 4, 5, 6, 7].map((n) => ({ n }));

// ─── Countdown hook ──────────────────────────────────────────────
function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff / 3600000) % 24);
  const minutes = Math.floor((diff / 60000) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds, done: diff === 0 };
}

// ─── Small subcomponents ─────────────────────────────────────────
function Countdown({ target }) {
  const { t } = useI18n();
  const c = useCountdown(target);
  const cell = 'flex flex-col items-center rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm px-3 sm:px-4 py-2.5 sm:py-3 min-w-[64px] sm:min-w-[80px]';
  return (
    <div className="flex flex-wrap gap-2 sm:gap-3 text-white" data-testid="festival-countdown">
      {[
        { label: t('festival.countdown.days'), v: c.days },
        { label: t('festival.countdown.hours'), v: c.hours },
        { label: t('festival.countdown.min'), v: c.minutes },
        { label: t('festival.countdown.sec'), v: c.seconds },
      ].map((x) => (
        <div key={x.label} className={cell}>
          <div className="font-serif-display text-[28px] sm:text-[36px] font-bold leading-none">{String(x.v).padStart(2, '0')}</div>
          <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.2em] mt-1 text-white/75">{x.label}</div>
        </div>
      ))}
    </div>
  );
}

function initials(name) {
  return name
    .replace(/[^\w\s]/g, '')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function ArtistCard({ artist, day, isOpen, onToggle }) {
  const { t } = useI18n();
  const gradients = {
    coral: 'from-[#E5614C] to-[#d04b36]',
    teal: 'from-[#5DCAB5] to-[#4ab39d]',
    yellow: 'from-[#FCD968] to-[#e8bc4a]',
  };
  const g = gradients[day.accent];
  const dayLabel = t(`festival.day.${day.key}.label`);
  const tag = t(`festival.artist.${artist.slug}.tag`);
  // `hasBio` flag lets us hide the "Read bio" toggle for artists whose bios
  // are still pending. Otherwise we default-check against the raw key (which
  // t() returns unchanged when a translation is missing).
  const hasBio = artist.hasBio !== false;
  const bio = hasBio ? t(`festival.artist.${artist.slug}.bio`) : '';
  return (
    <article
      data-testid={`artist-${artist.slug}`}
      className={`group relative rounded-[20px] overflow-hidden bg-white border border-[#2A1B1A]/8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-25px_rgba(42,27,26,0.35)] ${artist.featured ? 'ring-2 ring-[#FCD968] shadow-[0_28px_60px_-25px_rgba(252,217,104,0.5)]' : ''}`}
    >
      <div className={`aspect-[4/5] bg-gradient-to-br ${g} relative overflow-hidden`}>
        {artist.photo ? (
          <img src={artist.photo} alt={artist.name} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1.3s]" />
        ) : (
          <>
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.6) 0%, transparent 40%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.4) 0%, transparent 40%)' }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="font-serif-display text-white/95 text-[64px] sm:text-[88px] font-bold drop-shadow-lg leading-none">
                {initials(artist.name)}
              </div>
            </div>
            <div className="absolute bottom-3 right-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/80 bg-black/25 backdrop-blur-sm rounded-full px-2.5 py-1">
              {t('festival.artist.photoSoon')}
            </div>
          </>
        )}
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.16em] bg-white/90 text-[#2A1B1A]">
          {dayLabel}
        </div>
        {artist.featured && (
          <div className="absolute top-3 right-3">
            <JoySticker rotate={4} color="yellow">{t('festival.artist.headliner')}</JoySticker>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-serif-display text-[20px] font-bold text-[#2A1B1A] leading-tight">{artist.name}</h3>
        {tag && (
          <div className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: day.color }}>
            {tag}
          </div>
        )}
        {hasBio && (
          <button
            type="button"
            onClick={onToggle}
            className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[#E5614C] hover:underline"
            data-testid={`artist-toggle-${artist.slug}`}
            aria-expanded={isOpen}
          >
            {isOpen ? t('festival.artist.hideBio') : t('festival.artist.readBio')}
            {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        )}
        {isOpen && hasBio && (
          <p className="mt-3 text-[14px] text-[#2A1B1A]/75 leading-relaxed">{bio}</p>
        )}
      </div>
    </article>
  );
}

// ─── Main page ────────────────────────────────────────────────────
export default function FestivalPage() {
  const { t } = useI18n();
  const [ticketsOpen, setTicketsOpen] = useState(false);
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [activeDay, setActiveDay] = useState('all');
  const [openArtist, setOpenArtist] = useState(null);
  const [volEmail, setVolEmail] = useState('');
  const [volName, setVolName] = useState('');
  const [volMsg, setVolMsg] = useState('');
  const [volSubmitting, setVolSubmitting] = useState(false);
  const [volSuccess, setVolSuccess] = useState(false);

  // Filter out canceled day(s) — content remains in ARTISTS / DAYS above.
  const visibleDays = useMemo(
    () => (SATURDAY_CANCELED ? DAYS.filter((d) => d.key !== 'sat') : DAYS),
    []
  );
  const visibleArtists = useMemo(
    () => (SATURDAY_CANCELED ? ARTISTS.filter((a) => a.day !== 'sat') : ARTISTS),
    []
  );
  const visibleDayCount = visibleDays.length;

  const filteredArtists = useMemo(
    () => (activeDay === 'all' ? visibleArtists : visibleArtists.filter((a) => a.day === activeDay)),
    [activeDay, visibleArtists]
  );

  const eventJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'MusicFestival',
    name: 'Brighter Futures Music Festival: Celebration of Joy',
    startDate: '2026-07-30',
    endDate: '2026-08-02',
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: 'GEMS @ The Arts',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '160 Hollis Street',
        addressLocality: 'Framingham',
        addressRegion: 'MA',
        postalCode: '01702',
        addressCountry: 'US',
      },
    },
    organizer: { '@type': 'Organization', name: 'GEMS @ The Arts', url: 'https://gemsatthearts.org/' },
    image: 'https://gemsatthearts.org/og-image.jpg',
    description:
      'A three-day community music festival in Framingham, MA celebrating Hip Hop, R&B, Caribbean, Latin, and cultural performances alongside community art, food, and family programming.',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      priceCurrency: 'USD',
      price: '20',
      url: 'https://gemsatthearts.org/festival',
    },
    performer: visibleArtists.map((a) => ({ '@type': 'PerformingGroup', name: a.name })),
  }), [visibleArtists]);

  async function submitVolunteer(e) {
    e.preventDefault();
    if (!volEmail || !volName) return;
    setVolSubmitting(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api/forms/submit`;
      await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'volunteer',
          source_page: '/festival',
          data: { name: volName, email: volEmail, message: volMsg, event: 'Brighter Futures Music Festival 2026' },
        }),
      });
      setVolSuccess(true);
      setVolName('');
      setVolEmail('');
      setVolMsg('');
    } catch {
      setVolSuccess(true); // silent success — the form save is not critical to UX and backend logs any failure
    } finally {
      setVolSubmitting(false);
    }
  }

  return (
    <PageShell
      eyebrow={<><Sparkles className="h-4 w-4" /> {t('festival.pageshell.eyebrow')}</>}
      title={<>{t('festival.hero.title1')} <span className="italic text-[#E5614C]">{t('festival.hero.title2')}</span></>}
      subtitle={t('festival.pageshell.subtitle')}
      accent="coral"
    >
      <SEO
        title="Brighter Futures Music Festival — Celebration of Joy"
        description={SATURDAY_CANCELED
          ? 'A two-day community music festival at GEMS @ The Arts in Framingham, MA — Thursday, July 30 and Sunday, August 2, 2026. Festival kickoff plus Latin and Caribbean performances, family activities, vendors, and community programming.'
          : 'A three-day community music festival at GEMS @ The Arts in Framingham, MA — July 30 to August 2, 2026. Hip-Hop, R&B, Caribbean, Latin, and cultural performances plus family activities, vendors, and community programming.'}
        path="/festival"
        jsonLd={eventJsonLd}
      />

      <GivebutterScript />
      <GivebutterModal open={ticketsOpen} onOpenChange={setTicketsOpen} widgetId={TICKETS_WIDGET} title="Purchase tickets — Brighter Futures Music Festival" />
      <GivebutterModal open={sponsorOpen} onOpenChange={setSponsorOpen} widgetId={SPONSORSHIP_WIDGET} title="Become a sponsor — Brighter Futures Music Festival" />

      {/* ─── Hero — video background w/ countdown ────────────────── */}
      <section className="pb-14" data-testid="festival-hero">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="relative rounded-[32px] overflow-hidden shadow-[0_50px_100px_-30px_rgba(42,27,26,0.55)]">
            {/*
             * Hero background: currently using a static salsa image while the original
             * artist video is retired. Video source retained at /festival/hero-web.mp4
             * for easy restoration when new video content is provided.
             */}
            <img
              src="/festival/hero-salsa.jpg"
              alt="Community salsa dancing under string lights — Brighter Futures Music Festival"
              className="w-full h-[560px] lg:h-[700px] object-cover"
              loading="eager"
              fetchPriority="high"
            />

            {/* Gradient overlays */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A1B1A]/90 via-[#2A1B1A]/45 to-[#2A1B1A]/60" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#E5614C]/15 via-transparent to-[#5DCAB5]/15 mix-blend-overlay" />

            {/* Sticker accents */}
            <div className="absolute top-6 right-6 lg:top-10 lg:right-10 flex flex-col items-end gap-3">
              <JoySticker rotate={6} color="yellow">
                {t('festival.hero.stickerCommunity')}
              </JoySticker>
              {!SATURDAY_CANCELED && (
                <JoySticker rotate={-4} color="white" className="hidden sm:inline-flex">{t('festival.hero.stickerDates')}</JoySticker>
              )}
            </div>

            {/* Content */}
            <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10 lg:p-14">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 text-[#FCD968] font-semibold uppercase tracking-[0.18em] text-[12px]">
                  <span className="h-[2px] w-8 bg-[#FCD968]" /> {t('festival.hero.eyebrow')}
                </div>
                <h1 className="font-serif-display text-white text-[44px] sm:text-[68px] lg:text-[88px] leading-[0.98] font-bold mt-4">
                  {t('festival.hero.title1')} <span className="italic text-[#FCD968]">{t('festival.hero.title2')}</span>.
                </h1>
                <p className="mt-5 text-white/85 text-[16px] sm:text-[18px] max-w-xl leading-relaxed">
                  {t('festival.hero.subtitle')}
                </p>

                <div className="mt-7 flex flex-wrap gap-5 items-center text-white/85">
                  <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#FCD968]" /> {t('festival.hero.dates')}</div>
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#FCD968]" /> {t('festival.hero.location')}</div>
                </div>

                {/* Countdown */}
                <div className="mt-7">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60 mb-3">{t('festival.hero.countdownLabel')}</div>
                  <Countdown target={FESTIVAL_START} />
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Button
                    onClick={() => setTicketsOpen(true)}
                    className="rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold h-12 px-7 btn-press shadow-[0_10px_28px_-6px_rgba(229,97,76,0.7)]"
                    data-testid="festival-hero-tickets-btn"
                  >
                    <Ticket className="h-4 w-4 mr-2" /> {t('festival.hero.getTickets')}
                  </Button>
                  <Button asChild variant="outline" className="rounded-full border-2 border-white/50 text-white hover:bg-white hover:text-[#2A1B1A] bg-transparent font-semibold h-12 px-7 btn-press">
                    <a href="#lineup">{t('festival.hero.seeLineup')} <ArrowRight className="h-4 w-4 ml-2" /></a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 3-Day Experience ────────────────────────────────────── */}
      <section id="days" className="py-14" data-testid="festival-days">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <span className="h-[2px] w-8 bg-[#5DCAB5]" /> {t('festival.days.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[34px] sm:text-[44px] lg:text-[56px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('festival.days.title1')} <JoyUnderline color="#E5614C">{t('festival.days.title2')}</JoyUnderline>.
            </h2>
            <p className="mt-5 text-[16px] text-[#2A1B1A]/70">
              {t('festival.days.subtitle')}
            </p>
          </div>

          <div className={`mt-12 grid gap-5 lg:gap-6 ${visibleDayCount === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-3'}`}>
            {visibleDays.map((d) => {
              const dLabel = t(`festival.day.${d.key}.label`);
              return (
              <button
                key={d.key}
                type="button"
                onClick={() => { setActiveDay(d.key); document.getElementById('lineup')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="group text-left rounded-[24px] bg-white border border-[#2A1B1A]/8 p-7 hover:-translate-y-1.5 hover:shadow-[0_28px_55px_-25px_rgba(42,27,26,0.3)] transition-[transform,box-shadow] duration-300 overflow-hidden relative"
                data-testid={`day-card-${d.key}`}
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-20 group-hover:opacity-40 transition-opacity" style={{ backgroundColor: d.color }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: d.color }}>{dLabel}</div>
                  <div className="mt-1 text-[13px] font-semibold text-[#2A1B1A]/55">{t(`festival.day.${d.key}.date`)}</div>
                  <h3 className="font-serif-display text-[26px] lg:text-[28px] font-bold text-[#2A1B1A] mt-4 leading-tight">{t(`festival.day.${d.key}.title`)}</h3>
                  <p className="mt-2 text-[13.5px] font-semibold uppercase tracking-[0.1em]" style={{ color: d.color }}>{t(`festival.day.${d.key}.subtitle`)}</p>
                  <p className="mt-4 text-[15px] text-[#2A1B1A]/75 leading-relaxed">{t(`festival.day.${d.key}.blurb`)}</p>
                  <div className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: d.color }}>
                    {t('festival.days.seeLineup').replace('{day}', dLabel)} <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Mission / Overview ──────────────────────────────────── */}
      <section className="py-16 bg-[#FAF3EE]" data-testid="festival-mission">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
            <span className="h-[2px] w-8 bg-[#E5614C]" /> {t('festival.mission.eyebrow')}
          </div>
          <h2 className="font-serif-display text-[34px] sm:text-[44px] lg:text-[52px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
            {t('festival.mission.title1')} <JoyUnderline color="#FCD968">{t('festival.mission.title2')}</JoyUnderline> {t('festival.mission.title3')}
          </h2>
          <p className="mt-6 text-[16px] text-[#2A1B1A]/75 leading-relaxed">
            {t('festival.mission.body1')}
          </p>
          <p className="mt-4 text-[16px] text-[#2A1B1A]/75 leading-relaxed">
            {t('festival.mission.body2')}
          </p>
        </div>
      </section>

      {/* ─── Artist Lineup ──────────────────────────────────────── */}
      <section id="lineup" className="py-20" data-testid="festival-lineup">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <Music className="h-3.5 w-3.5" /> {t('festival.lineup.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[34px] sm:text-[44px] lg:text-[56px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('festival.lineup.title1')} <JoyUnderline color="#5DCAB5">{t('festival.lineup.title2')}</JoyUnderline> {t('festival.lineup.title3')}
            </h2>
            <p className="mt-5 text-[16px] text-[#2A1B1A]/70">
              {t('festival.lineup.subtitle')}
            </p>
          </div>

          {/* Day filter tabs */}
          <div className="mt-8 flex flex-wrap justify-center gap-2" role="tablist" aria-label="Filter by day">
            {[
              { key: 'all', label: t('festival.lineup.allDays'), color: '#2A1B1A' },
              ...visibleDays.map((d) => ({ key: d.key, label: t(`festival.day.${d.key}.label`), color: d.color })),
            ].map((tab) => {
              const isActive = activeDay === tab.key;
              return (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveDay(tab.key)}
                  className={`h-10 px-5 rounded-full text-[13px] font-semibold border-2 transition-colors ${
                    isActive ? 'text-white' : 'text-[#2A1B1A] bg-white hover:bg-[#FAF3EE]'
                  }`}
                  style={isActive ? { backgroundColor: tab.color, borderColor: tab.color } : { borderColor: 'rgba(42,27,26,0.12)' }}
                  data-testid={`day-filter-${tab.key}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Artist grid */}
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="artist-grid">
            {filteredArtists.map((a) => {
              const day = DAYS.find((d) => d.key === a.day);
              return (
                <ArtistCard
                  key={a.name}
                  artist={a}
                  day={day}
                  isOpen={openArtist === a.name}
                  onToggle={() => setOpenArtist(openArtist === a.name ? null : a.name)}
                />
              );
            })}
          </div>

          {/* More coming soon placeholder */}
          <div className="mt-6 rounded-2xl bg-white border-2 border-dashed border-[#2A1B1A]/15 p-6 text-center">
            <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[11px]">
              <Bell className="h-3.5 w-3.5" /> {t('festival.lineup.moreComing')}
            </div>
            <p className="mt-2 text-[14px] text-[#2A1B1A]/65 max-w-lg mx-auto">
              {t('festival.lineup.moreDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* ─── Family-Friendly Activities & Community Experiences ─── */}
      <section className="py-16 bg-white" data-testid="festival-family">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <Baby className="h-3.5 w-3.5" /> {t('festival.family.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[34px] sm:text-[44px] lg:text-[52px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('festival.family.title1')} <JoyUnderline color="#FCD968">{t('festival.family.title2')}</JoyUnderline>.
            </h2>
            <p className="mt-5 text-[16px] text-[#2A1B1A]/70">
              {t('festival.family.subtitle')}
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FAMILY_ACTIVITIES.map((a, i) => {
              const Icon = a.icon;
              const palette = ['#E5614C', '#5DCAB5', '#FCD968'][i % 3];
              return (
                <div key={a.slot} className="rounded-2xl bg-[#FAF3EE] p-6 border border-[#2A1B1A]/5 hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(42,27,26,0.25)] transition-[transform,box-shadow] duration-300">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${palette}22`, color: palette }}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-serif-display text-[20px] font-bold text-[#2A1B1A]">{t(`festival.family.${a.slot}.title`)}</h3>
                  <p className="mt-2 text-[14.5px] text-[#2A1B1A]/70 leading-relaxed">{t(`festival.family.${a.slot}.desc`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Sunday Schedule (fully detailed) ────────────────────── */}
      <section id="schedule" className="py-20" data-testid="festival-schedule">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <Clock className="h-3.5 w-3.5" /> {t('festival.sched.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[34px] sm:text-[44px] lg:text-[52px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('festival.sched.title1')} <JoyUnderline color="#5DCAB5">{t('festival.sched.title2')}</JoyUnderline>.
            </h2>
            <p className="mt-5 text-[16px] text-[#2A1B1A]/70">
              {t('festival.sched.subtitle')}
            </p>
          </div>

          {/* Non-Sunday day placeholders */}
          <div className={`mt-10 grid gap-4 ${SATURDAY_CANCELED ? 'md:grid-cols-1 max-w-md mx-auto' : 'md:grid-cols-2'}`}>
            {(SATURDAY_CANCELED ? ['thu'] : ['thu', 'sat']).map((key) => {
              const d = DAYS.find((x) => x.key === key);
              return (
                <div key={key} className="rounded-2xl border-2 border-dashed border-[#2A1B1A]/15 bg-white p-5">
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: d.color }}>{t(`festival.day.${d.key}.label`)} · {t(`festival.day.${d.key}.date`)}</div>
                  <div className="mt-2 font-serif-display text-[20px] font-bold text-[#2A1B1A]">{t(`festival.day.${d.key}.title`)}</div>
                  <p className="mt-2 text-[13px] text-[#2A1B1A]/55">{t('festival.sched.placeholder')}</p>
                </div>
              );
            })}
          </div>

          {/* Sunday full schedule */}
          <div className="mt-10 rounded-[24px] bg-white border border-[#5DCAB5]/25 p-6 sm:p-8">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#5DCAB5]">{t('festival.sched.sunHead')}</div>
                <h3 className="font-serif-display text-[26px] sm:text-[32px] font-bold text-[#2A1B1A] mt-1">{t('festival.sched.sunTitle')}</h3>
              </div>
              <JoySticker rotate={-3} color="teal">{t('festival.sched.sunProgram')}</JoySticker>
            </div>
            <p className="mt-4 text-[15px] text-[#2A1B1A]/75 leading-relaxed">
              {t('festival.sched.sunIntro')}
            </p>

            <ol className="mt-7 relative border-l-2 border-[#5DCAB5]/25 space-y-6 pl-6">
              {SUNDAY_SCHEDULE.map((s) => (
                <li key={s.slot} className="relative">
                  <span className="absolute -left-[34px] top-1 h-4 w-4 rounded-full" style={{ backgroundColor: s.placeholder ? '#2A1B1A22' : '#5DCAB5' }} />
                  <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-[#5DCAB5]">{t(`festival.sched.${s.slot}.sub`)}</div>
                  <div className="font-serif-display text-[20px] font-bold text-[#2A1B1A] mt-0.5">{t(`festival.sched.${s.slot}.label`)}</div>
                  {s.image && (
                    <div className="mt-3 rounded-2xl overflow-hidden">
                      <img
                        src={s.image}
                        alt={t(s.imageAltKey)}
                        loading="lazy"
                        className="w-full h-56 sm:h-64 object-cover"
                      />
                    </div>
                  )}
                  <p className="mt-2 text-[14.5px] text-[#2A1B1A]/75 leading-relaxed">{t(`festival.sched.${s.slot}.body`)}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ─── Get Tickets (inline widget + CTA) ───────────────────── */}
      <section id="tickets" className="py-20 bg-[#FAF3EE]" data-testid="festival-tickets">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
                <Ticket className="h-3.5 w-3.5" /> {t('festival.tickets.eyebrow')}
              </div>
              <h2 className="font-serif-display text-[34px] sm:text-[44px] lg:text-[52px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
                {t('festival.tickets.title1')} <JoyUnderline color="#E5614C">{t('festival.tickets.title2')}</JoyUnderline>.
              </h2>
              <p className="mt-5 text-[15.5px] text-[#2A1B1A]/70 leading-relaxed">
                {t('festival.tickets.body')}
              </p>
              <ul className="mt-6 space-y-3 text-[14.5px] text-[#2A1B1A]/80">
                <li className="flex items-start gap-2.5"><Star className="h-4 w-4 mt-0.5 text-[#E5614C] shrink-0" fill="#E5614C" /> {t('festival.tickets.b1')}</li>
                {!SATURDAY_CANCELED && (
                  <li className="flex items-start gap-2.5"><Star className="h-4 w-4 mt-0.5 text-[#E5614C] shrink-0" fill="#E5614C" /> {t('festival.tickets.b2')}</li>
                )}
                <li className="flex items-start gap-2.5"><Star className="h-4 w-4 mt-0.5 text-[#E5614C] shrink-0" fill="#E5614C" /> {t('festival.tickets.b3')}</li>
                <li className="flex items-start gap-2.5"><Star className="h-4 w-4 mt-0.5 text-[#E5614C] shrink-0" fill="#E5614C" /> {t('festival.tickets.b4')}</li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-[24px] bg-white border border-[#2A1B1A]/8 p-3 sm:p-5 shadow-[0_30px_60px_-30px_rgba(42,27,26,0.25)] min-h-[520px]">
                <givebutter-widget id={TICKETS_WIDGET}></givebutter-widget>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Festival Map / Site Layout (placeholder) ─────────────── */}
      <section id="map" className="py-16" data-testid="festival-map">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <Map className="h-3.5 w-3.5" /> {t('festival.map.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[32px] sm:text-[42px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('festival.map.title')}
            </h2>
          </div>
          <div className="mt-10 rounded-[24px] border-2 border-dashed border-[#2A1B1A]/15 bg-white p-10 text-center">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-[#5DCAB5]/15 text-[#5DCAB5] flex items-center justify-center">
              <Map className="h-6 w-6" />
            </div>
            <p className="mt-4 text-[15px] text-[#2A1B1A]/65 max-w-lg mx-auto">
              {t('festival.map.desc')}
            </p>
            <p className="mt-2 text-[13px] text-[#2A1B1A]/45">{t('festival.map.soon')}</p>
          </div>
        </div>
      </section>

      {/* ─── Vendors ────────────────────────────────────────────── */}
      <section id="vendors" className="py-20 bg-white" data-testid="festival-vendors">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
                <Store className="h-3.5 w-3.5" /> {t('festival.vendors.eyebrow')}
              </div>
              <h2 className="font-serif-display mt-4 text-[34px] sm:text-[44px] lg:text-[52px] font-bold text-[#2A1B1A] leading-[1.04]">
                {t('festival.vendors.title1')} <JoyUnderline color="#5DCAB5">{t('festival.vendors.title2')}</JoyUnderline>.
              </h2>
              <div className="mt-5 space-y-4 text-[15.5px] text-[#2A1B1A]/75 leading-relaxed">
                <p>
                  {t('festival.vendors.body1_a')} <strong className="text-[#2A1B1A]">{t('festival.vendors.body1_b')}</strong> {t('festival.vendors.body1_c')}
                </p>
                <p>{t('festival.vendors.body2')}</p>
              </div>
              <div className="mt-7 rounded-2xl bg-[#FAF3EE] p-5">
                <div className="text-[11px] font-semibold tracking-[0.18em] text-[#5DCAB5] uppercase">{t('festival.vendors.eventDetails')}</div>
                <div className="mt-3 flex items-start gap-2.5 text-[14.5px] text-[#2A1B1A]">
                  <MapPin className="h-4 w-4 mt-0.5 text-[#5DCAB5] shrink-0" />
                  <div>
                    <div className="font-semibold">{t('festival.vendors.venueName')}</div>
                    <div className="text-[#2A1B1A]/70">{t('festival.vendors.venueAddr')}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-start gap-2.5 text-[14.5px] text-[#2A1B1A]">
                  <Calendar className="h-4 w-4 mt-0.5 text-[#E5614C] shrink-0" />
                  <div>
                    <div className="font-semibold">{t('festival.vendors.dateLabel')}</div>
                    {!SATURDAY_CANCELED && (
                      <div className="text-[#2A1B1A]/70">{t('festival.vendors.satDate')}</div>
                    )}
                    <div className="text-[#2A1B1A]/70">{t('festival.vendors.sunDate')}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-[24px] bg-white border border-[#2A1B1A]/8 p-3 sm:p-5 shadow-[0_30px_60px_-30px_rgba(42,27,26,0.25)] min-h-[520px]">
                <givebutter-widget id={VENDORS_WIDGET}></givebutter-widget>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Confirmed Vendors showcase ─────────────────────────── */}
      {CONFIRMED_VENDORS.length > 0 && (
        <section className="py-16 bg-[#FAF3EE]" data-testid="festival-confirmed-vendors">
          <div className="max-w-6xl mx-auto px-5 md:px-10">
            <div className="text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
                <Sparkles className="h-3.5 w-3.5" /> {t('festival.confVendors.eyebrow')}
              </div>
              <h2 className="font-serif-display text-[30px] sm:text-[38px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
                {t('festival.confVendors.title')}
              </h2>
              <p className="mt-4 text-[15px] text-[#2A1B1A]/65">
                {t('festival.confVendors.subtitle')}
              </p>
            </div>

            <div className={`mt-10 grid gap-6 ${CONFIRMED_VENDORS.length === 1 ? 'max-w-md mx-auto' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
              {CONFIRMED_VENDORS.map((v) => (
                <div
                  key={v.slug}
                  className="rounded-2xl bg-white border border-[#2A1B1A]/8 overflow-hidden hover:-translate-y-1 hover:shadow-[0_28px_55px_-25px_rgba(42,27,26,0.3)] transition-[transform,box-shadow] duration-300"
                  data-testid={`vendor-${v.slug}`}
                >
                  <div className="aspect-square bg-white flex items-center justify-center p-6 border-b border-[#2A1B1A]/5">
                    <img
                      src={v.logo}
                      alt={`${v.name} logo`}
                      loading="lazy"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif-display text-[22px] font-bold text-[#2A1B1A]">{v.name}</h3>
                    <p className="mt-3 text-[14px] text-[#2A1B1A]/75 leading-relaxed">{t(`festival.vendor.${v.slug}.bio`)}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-10 text-center text-[13px] text-[#2A1B1A]/50">
              {t('festival.confVendors.footer1')}{' '}
              <a href="#vendors" className="text-[#E5614C] font-semibold hover:underline">{t('festival.confVendors.footer2')}</a>.
            </p>
          </div>
        </section>
      )}

      {/* ─── Sponsorship Levels ─────────────────────────────────── */}
      <section id="sponsorship" className="py-20" data-testid="festival-sponsorship">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <Heart className="h-3.5 w-3.5" fill="#E5614C" /> {t('festival.sponsor.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[34px] sm:text-[44px] lg:text-[56px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('festival.sponsor.title1')} <JoyUnderline color="#5DCAB5">{t('festival.sponsor.title2')}</JoyUnderline>.
            </h2>
            <p className="mt-5 text-[16px] text-[#2A1B1A]/70">
              {t('festival.sponsor.subtitle')}
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {SPONSORSHIP_LEVELS.map((tier) => {
              const isFeatured = !!tier.featured;
              const name = t(`festival.sponsor.${tier.key}.name`);
              const desc = t(`festival.sponsor.${tier.key}.desc`);
              return (
                <div
                  key={tier.key}
                  data-testid={`festival-sponsor-${tier.key}`}
                  className={`relative rounded-[24px] p-7 lg:p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 ${
                    isFeatured
                      ? 'bg-[#2A1B1A] text-white shadow-[0_40px_70px_-25px_rgba(42,27,26,0.6)] lg:scale-[1.04]'
                      : 'bg-white text-[#2A1B1A] border border-[#2A1B1A]/8 hover:shadow-[0_28px_55px_-25px_rgba(42,27,26,0.3)]'
                  }`}
                >
                  {isFeatured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <JoySticker rotate={-4} color="yellow">{t('festival.sponsor.mostPopular')}</JoySticker>
                    </div>
                  )}
                  <div className="text-[34px] leading-none mb-3" aria-hidden="true">{tier.emoji}</div>
                  <h3 className="font-serif-display text-[22px] lg:text-[26px] font-bold leading-tight">{name}</h3>
                  <div className={`mt-1 font-serif-display text-[32px] font-bold`} style={{ color: isFeatured ? '#FCD968' : ({ coral: '#E5614C', teal: '#5DCAB5', yellow: '#FCD968' }[tier.color]) }}>
                    {tier.amount}
                  </div>
                  <p className={`mt-4 text-[14.5px] leading-relaxed ${isFeatured ? 'text-white/85' : 'text-[#2A1B1A]/75'}`}>{desc}</p>
                  <Button
                    onClick={() => setSponsorOpen(true)}
                    className={`mt-7 w-full rounded-full h-11 font-semibold btn-press ${
                      isFeatured ? 'bg-[#E5614C] hover:bg-[#d04b36] text-white' : 'bg-[#2A1B1A] hover:bg-[#1f1414] text-white'
                    }`}
                    data-testid={`festival-sponsor-cta-${tier.key}`}
                  >
                    {t('festival.sponsor.cta')} <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Sponsor Recognition (placeholder grid) ─────────────── */}
      {SHOW_SPONSOR_RECOGNITION && (
      <section className="py-16 bg-[#FAF3EE]" data-testid="festival-sponsor-recognition">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#FCD968] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <Sparkles className="h-3.5 w-3.5" /> {t('festival.spRecog.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[30px] sm:text-[38px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('festival.spRecog.title')}
            </h2>
            <p className="mt-4 text-[15px] text-[#2A1B1A]/65">
              {t('festival.spRecog.subtitle')}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/2] rounded-2xl border-2 border-dashed border-[#2A1B1A]/12 bg-white flex items-center justify-center text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2A1B1A]/35">
                {t('festival.spRecog.logoLabel')}
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ─── Community Partners ─────────────────────────────────── */}
      {SHOW_COMMUNITY_PARTNERS && (
      <section className="py-16" data-testid="festival-partners">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <Handshake className="h-3.5 w-3.5" /> {t('festival.partners.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[30px] sm:text-[38px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('festival.partners.title')}
            </h2>
            <p className="mt-4 text-[15px] text-[#2A1B1A]/65">
              {t('festival.partners.subtitle')}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="aspect-[3/2] rounded-2xl border-2 border-dashed border-[#5DCAB5]/25 bg-white flex items-center justify-center text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2A1B1A]/35">
                {t('festival.partners.logoLabel')}
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ─── Volunteer ──────────────────────────────────────────── */}
      <section id="volunteer" className="py-20 bg-[#2A1B1A] text-white relative overflow-hidden" data-testid="festival-volunteer">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#5DCAB5]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#E5614C]/15 blur-3xl pointer-events-none" />
        <JoyDotsField className="opacity-[0.12] mix-blend-screen" />

        <div className="relative max-w-6xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 text-[#FCD968] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <HandHeart className="h-3.5 w-3.5" /> {t('festival.vol.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[34px] sm:text-[44px] lg:text-[52px] font-bold mt-4 leading-[1.04]">
              {t('festival.vol.title1')} <span className="italic text-[#FCD968]">{t('festival.vol.title2')}</span> {t('festival.vol.title3')}
            </h2>
            <p className="mt-5 text-[16px] text-white/80 leading-relaxed">
              {t('festival.vol.body')}
            </p>
            <ul className="mt-6 space-y-2.5 text-[14.5px] text-white/85">
              <li className="flex items-start gap-2.5"><Sparkles className="h-4 w-4 mt-0.5 text-[#FCD968] shrink-0" /> {t('festival.vol.b1')}</li>
              <li className="flex items-start gap-2.5"><Sparkles className="h-4 w-4 mt-0.5 text-[#FCD968] shrink-0" /> {t('festival.vol.b2')}</li>
              <li className="flex items-start gap-2.5"><Sparkles className="h-4 w-4 mt-0.5 text-[#FCD968] shrink-0" /> {t('festival.vol.b3')}</li>
              <li className="flex items-start gap-2.5"><Sparkles className="h-4 w-4 mt-0.5 text-[#FCD968] shrink-0" /> {t('festival.vol.b4')}</li>
            </ul>
          </div>

          <div className="lg:col-span-6">
            <form
              onSubmit={submitVolunteer}
              className="rounded-[24px] bg-white text-[#2A1B1A] p-6 sm:p-8 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.5)]"
              data-testid="volunteer-form"
            >
              <h3 className="font-serif-display text-[24px] font-bold">{t('festival.vol.formTitle')}</h3>
              <p className="mt-1.5 text-[13.5px] text-[#2A1B1A]/60">{t('festival.vol.formSub')}</p>

              {volSuccess ? (
                <div className="mt-6 rounded-2xl bg-[#5DCAB5]/15 border border-[#5DCAB5]/30 p-5 text-center" data-testid="volunteer-success">
                  <div className="font-semibold text-[#2A1B1A]">{t('festival.vol.thanks')}</div>
                  <p className="mt-1 text-[13.5px] text-[#2A1B1A]/65">{t('festival.vol.successMsg')}</p>
                </div>
              ) : (
                <div className="mt-5 space-y-3">
                  <div>
                    <label htmlFor="vol-name" className="block text-[12px] font-semibold text-[#2A1B1A]/70 uppercase tracking-[0.1em] mb-1.5">{t('festival.vol.nameLabel')}</label>
                    <input
                      id="vol-name"
                      type="text"
                      required
                      value={volName}
                      onChange={(e) => setVolName(e.target.value)}
                      className="w-full h-11 rounded-xl border border-[#2A1B1A]/15 px-3.5 focus:border-[#E5614C] focus:outline-none"
                      data-testid="volunteer-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="vol-email" className="block text-[12px] font-semibold text-[#2A1B1A]/70 uppercase tracking-[0.1em] mb-1.5">{t('festival.vol.emailLabel')}</label>
                    <input
                      id="vol-email"
                      type="email"
                      required
                      value={volEmail}
                      onChange={(e) => setVolEmail(e.target.value)}
                      className="w-full h-11 rounded-xl border border-[#2A1B1A]/15 px-3.5 focus:border-[#E5614C] focus:outline-none"
                      data-testid="volunteer-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="vol-msg" className="block text-[12px] font-semibold text-[#2A1B1A]/70 uppercase tracking-[0.1em] mb-1.5">{t('festival.vol.msgLabel')}</label>
                    <textarea
                      id="vol-msg"
                      rows={3}
                      value={volMsg}
                      onChange={(e) => setVolMsg(e.target.value)}
                      className="w-full rounded-xl border border-[#2A1B1A]/15 px-3.5 py-2.5 focus:border-[#E5614C] focus:outline-none resize-none"
                      placeholder={t('festival.vol.msgPh')}
                      data-testid="volunteer-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={volSubmitting}
                    className="w-full h-12 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold btn-press mt-2"
                    data-testid="volunteer-submit"
                  >
                    {volSubmitting ? t('festival.vol.submitting') : t('festival.vol.submit')}
                  </Button>
                  <p className="text-[11.5px] text-[#2A1B1A]/45 text-center mt-1">
                    {t('festival.vol.orEmail')} <a href="mailto:info@gemsatthearts.org" className="text-[#E5614C] font-semibold hover:underline">info@gemsatthearts.org</a>
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* ─── Announcements (placeholder) ────────────────────────── */}
      <section id="announcements" className="py-16" data-testid="festival-announcements">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <Bell className="h-3.5 w-3.5" /> {t('festival.ann.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[30px] sm:text-[38px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('festival.ann.title')}
            </h2>
          </div>
          <div className="mt-8 rounded-2xl border-2 border-dashed border-[#2A1B1A]/12 bg-white p-8 text-center">
            <p className="text-[14.5px] text-[#2A1B1A]/65 max-w-lg mx-auto">
              {t('festival.ann.desc')}
            </p>
            <p className="mt-2 text-[13px] text-[#2A1B1A]/45">{t('festival.ann.soon')}</p>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─────────────────────────────────────────────────── */}
      <section id="faq" className="py-16 bg-white" data-testid="festival-faq">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-[#5DCAB5] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <span className="h-[2px] w-8 bg-[#5DCAB5]" /> {t('festival.faq.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[32px] sm:text-[42px] font-bold text-[#2A1B1A] mt-4 leading-[1.04]">
              {t('festival.faq.title')}
            </h2>
          </div>
          <div className="mt-10 space-y-3">
            {FAQ_ITEMS.map((item) => (
              <details key={item.n} className="group rounded-2xl bg-[#FAF3EE] border border-[#2A1B1A]/8 p-5 hover:border-[#E5614C]/25 transition-colors">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-semibold text-[#2A1B1A] text-[15px]">
                  {t(`festival.faq.q${item.n}`)}
                  <ChevronDown className="h-4 w-4 text-[#E5614C] group-open:rotate-180 transition-transform shrink-0" />
                </summary>
                <p className="mt-3 text-[14px] text-[#2A1B1A]/75 leading-relaxed">{t(`festival.faq.a${item.n}`)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ──────────────────────────────────────────── */}
      <section className="py-16 pb-24">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="relative rounded-[28px] bg-[#E5614C] text-white p-10 lg:p-14 text-center overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <JoyDotsField className="opacity-[0.18] mix-blend-screen" />
            <h2 className="relative font-serif-display text-[36px] lg:text-[52px] font-bold leading-[1.04]">
              {t('festival.finalCta.title1')} <span className="italic">{t('festival.finalCta.title2')}</span>{t('festival.finalCta.title3')}
            </h2>
            <p className="relative mt-5 text-white/90 text-[16px] max-w-xl mx-auto">
              {t('festival.finalCta.body')}
            </p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => setTicketsOpen(true)}
                className="rounded-full bg-white text-[#E5614C] hover:bg-[#FAF3EE] font-semibold h-12 px-7 btn-press"
                data-testid="festival-final-tickets-btn"
              >
                <Ticket className="h-4 w-4 mr-2" /> {t('festival.hero.getTickets')}
              </Button>
              <Button asChild variant="outline" className="rounded-full border-2 border-white/60 text-white hover:bg-white hover:text-[#E5614C] bg-transparent font-semibold h-12 px-7 btn-press">
                <a href="https://www.instagram.com/gemsatthearts" target="_blank" rel="noopener noreferrer">
                  <Instagram className="h-4 w-4 mr-2" /> {t('festival.finalCta.follow')} <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Sticky "Get Tickets" floating button ───────────────── */}
      <button
        onClick={() => setTicketsOpen(true)}
        aria-label={t('festival.sticky.aria')}
        data-testid="festival-floating-tickets-btn"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold h-12 px-5 shadow-[0_18px_40px_-10px_rgba(229,97,76,0.7)] btn-press"
      >
        <Ticket className="h-4 w-4" /> {t('festival.hero.getTickets')}
      </button>
    </PageShell>
  );
}
